// imports section Start
import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Grid,
  IconButton,
  Tabs,
  Tab,
  Dialog,
  DialogContent,
  CircularProgress,
  Alert,
  Drawer,
  Pagination,
} from "@mui/material";
import ListIcon from "@mui/icons-material/List";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import { useTheme } from "@mui/material/styles";
import { useStyles } from "../../assets/styles/Theme";

import Header from "../../components/common/Header";

import { BASEURL, IMAGEBASEURL } from "../../config/Config";
import {
  externalgetApi,
  externalpostApi,
} from "../../services/external/externalApis";
import {
  COMMON_API_02,
  CASHIER_API_03,
  CASHIER_API_02,
} from "../../api/ApiModel";

import { addCheffItemToCart } from "../../store/ReduxCheffCartReducer";
import {
  CON_STATUS_ACTIVE,
  CON_MENU_TYPE_CODE,
  CON_STATUS_KITCHEN,
} from "../../utils/sys/Constants";
import SnackbarMessage from "../../components/common/SnackbarMessage";
import { ADD_TO_CART } from "../../utils/sys/Messages";
import CheffSummary from "../../components/cheff/CheffSummary";
import CheffItemCard from "../../components/cheff/CheffItemCard";

// imports section End

const Cheff = () => {
  // Hooks start
  const theme = useTheme();
  const styles = useStyles(theme);
  const dispatch = useDispatch();
  const [allItems, setAllItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [data, setData] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false); // Control Snackbar visibility
  const [snackbarMessage, setSnackbarMessage] = useState(""); // Snackbar message
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    // if (isLoggedIn && COMPCODE && LOCCODE && RESTCODE) {
    fetchData();
    // }
  }, []);

  // Hooks end

  // Get user parameters from Redux store
  const SYS_USER = useSelector((state) => state.user) || {};
  const { COMPCODE, LOCCODE, RESTCODE, USERREFCODE } =
    SYS_USER.USER_PARAM || {};

  // Handlers start

  const itemsPerPage = 16; // Number of items per page
  // Check if user is logged in
  const isLoggedIn = !!USERREFCODE;

  const handleAddToCart = (item) => {
    if (isLoggedIn) {
      dispatch(addCheffItemToCart(item));
    } else {
      alert("Please log in to add items to the cart.");

      setSnackbarMessage(ADD_TO_CART);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    // Filter items based on the search query
    const filtered = allItems.filter(
      (item) =>
        item.MENUNAME.toLowerCase().includes(query.toLowerCase()) ||
        item.MENUREFCODE.toLowerCase().includes(query.toLowerCase())
    );
    // Sort items alphabetically by MENUNAME
    const sortedFilteredItems = filtered.sort((a, b) =>
      a.MENUNAME.localeCompare(b.MENUNAME)
    );
    setFilteredItems(sortedFilteredItems);
  };

  const handleViewOrder = (order) => setSelectedOrder(order);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };
  // Handlers end

  // API calls start
  const fetchData = async () => {
    try {
      const params = {
        COMPCODE,
        LOCCODE,
        RESTCODE,
        STS: CON_STATUS_ACTIVE,
        STSK: CON_STATUS_KITCHEN,
      };
      const response = await externalgetApi(COMMON_API_02, "GET", params);
      console.log("response items", response);
      const filteredItems = response?.filter(item => item.STS === CON_STATUS_KITCHEN);
      setAllItems(filteredItems);
      setFilteredItems(filteredItems);
    } catch (error) {
      setError(error);
    } finally {
      setData(true);
    }
  };
  // API calls end

  return (
    <div>
      <Box sx={styles.root}>
        <Grid container sx={styles.height}>
          <Grid
            item
            xs={12}
            sm={12}
            md={7}
            lg={8}
            xl={8}
            sx={styles.screen_content}
          >
            <Box
              sx={{
                backgroundColor: theme.palette.background.paper,
                width: "100%",
                padding: "12px 16px 0 16px",
                borderBottom: `2px solid ${theme.palette.stroke.main}`,
                boxShadow: 1,
              }}
            >
              <Header
                searchQuery={searchQuery}
                handleSearch={handleSearch}
                showSearch={true}
              />
            </Box>
            <Box
              sx={{
                ...styles.cards_container,
                borderTop: `0.1px solid ${theme.palette.stroke.main}`,
              }}
            >
              <Grid container spacing={4}>
                {data ? (
                  currentItems.map((item, index) => (
                    <Grid item xs={12} sm={6} md={6} lg={4} xl={3} key={index}>
                      <CheffItemCard
                        cheffItem={item}
                        IMAGEBASEURL={IMAGEBASEURL}
                        onViewOrder={handleViewOrder}
                        onAddToCart={handleAddToCart}
                      />
                    </Grid>
                  ))
                ) : (
                  <Box sx={styles.progress_circular}>
                    <CircularProgress />
                  </Box>
                )}
                {error && (
                  <Grid item xs={12}>
                    <Alert severity="error">
                      Error fetching data: {error.message}
                    </Alert>
                  </Grid>
                )}
              </Grid>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  pt: 2,
                }}
              >
                <Pagination
                  count={Math.ceil(filteredItems.length / itemsPerPage)}
                  page={currentPage}
                  onChange={(event, value) => setCurrentPage(value)}
                  color="primary"
                />
              </Box>
            </Box>
          </Grid>

          <Grid
            container
            xs={12}
            sm={12}
            md={5}
            lg={4}
            xl={4}
            sx={{
              ...styles.cs_aside,
            }}
          >
            <Box sx={{ position: "relative", height: "100%" }}>
              <CheffSummary order={selectedOrder} />
            </Box>
          </Grid>
        </Grid>
      </Box>
      <SnackbarMessage
        open={snackbarOpen}
        handleClose={handleCloseSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </div>
  );
};

export default Cheff;
